import * as React from 'react';
import { Failure, Request } from '../ApiClient';
import { ApiResult } from './ApiResult';
import { useFetch } from './useFetch';
import { Initial, Loading, Success } from '../Result';

export const useCacheSuccessfulApiResult = <Response, Error>(
	result: ApiResult<Response, Error>
): ApiResult<Response, Error> => {
	const prevResultRef = React.useRef<ApiResult<Response, Error>>();

	React.useEffect(() => {
		if (result instanceof Success) {
			prevResultRef.current = result;
		}
	}, [result]);

	if (result instanceof Success) {
		return result;
	}

	return prevResultRef.current || new Initial();
};

export const useApiResult = <R extends Request<Response>, Response, Error>(
	request: R | null,
	deps: React.DependencyList
): [ApiResult<Response, Error>, () => Promise<void>] => {
	const [[result, resultDeps], setResult] = React.useState<[ApiResult<Response, Error>, React.DependencyList]>([
		new Initial(),
		deps,
	]);

	const cancelled = React.useRef(false);
	React.useEffect(
		() => () => {
			cancelled.current = true;
		},
		[]
	);

	const fetch = useFetch<R, Response, Error>();

	const load = React.useCallback(async () => {
		const result: ApiResult<Response, Error> = request !== null ? await fetch(request) : new Initial();
		if (!cancelled.current) {
			setResult(([, deps]) => [result, deps]);
		}
	}, deps);

	const reload = async () => {
		setResult(([prevResult, deps]) => [new Loading(prevResult.value), deps]);
		await load();
	};

	React.useEffect(() => {
		setResult([new Loading<Response, Failure<Error>>(null), deps]);
		load();
	}, deps);

	const currentResult: ApiResult<Response, Error> = React.useMemo(
		() =>
			deps.length === resultDeps.length && !resultDeps.find((dep, index) => deps[index] !== dep)
				? result
				: new Initial(),
		[result, ...deps, ...resultDeps]
	);

	return [currentResult, reload];
};
